import React, { useEffect } from "react";
import { X, ExternalLink, Download, FileText } from "lucide-react";

/**
 * PdfViewerModal — overlay modal to preview a PDF inline (no page navigation).
 *
 * Props:
 *   open     : boolean
 *   pdfUrl   : string  — resolved absolute URL of the PDF
 *   title    : string  — optional header title
 *   onClose  : () => void
 */
export default function PdfViewerModal({ open, pdfUrl, title = "PDF Preview", onClose }) {
  // Prevent background scroll while modal open + ESC-to-close.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const src = pdfUrl ? `${pdfUrl}#toolbar=0&navpanes=0` : "";

  return (
    <div
      className="fixed inset-0 z-[95] flex items-center justify-center p-3 sm:p-6 animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      data-testid="pdf-viewer-modal"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close preview"
        onClick={onClose}
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        data-testid="pdf-viewer-backdrop"
      />

      {/* Card */}
      <div className="relative z-[1] w-full max-w-5xl h-[92vh] sm:h-[88vh] bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-neutral-200 bg-white">
          <FileText className="h-5 w-5 text-primary shrink-0" />
          <h2 className="flex-1 font-display font-semibold text-ink truncate">{title}</h2>
          {pdfUrl && (
            <>
              <a
                href={pdfUrl}
                download
                className="hidden sm:inline-flex items-center gap-1.5 text-xs font-medium text-ink hover:text-primary px-2.5 py-1.5 rounded-md hover:bg-neutral-100 transition-colors"
                data-testid="pdf-viewer-download"
              >
                <Download className="h-3.5 w-3.5" /> Download
              </a>
              <a
                href={pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hidden sm:inline-flex items-center gap-1.5 text-xs font-medium text-ink hover:text-primary px-2.5 py-1.5 rounded-md hover:bg-neutral-100 transition-colors"
                data-testid="pdf-viewer-open-tab"
              >
                <ExternalLink className="h-3.5 w-3.5" /> Open in new tab
              </a>
            </>
          )}
          <button
            type="button"
            onClick={onClose}
            className="h-9 w-9 rounded-full bg-neutral-100 hover:bg-neutral-200 text-ink flex items-center justify-center transition-colors"
            aria-label="Close"
            data-testid="pdf-viewer-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 bg-neutral-100">
          {pdfUrl ? (
            <iframe
              key={pdfUrl}
              src={src}
              title={title}
              className="w-full h-full"
              data-testid="pdf-viewer-iframe"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-sm text-muted-foreground">
              No PDF to display.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
